import React, { useCallback } from 'react'

import {
  CONVERSATION_FACTS_DIMENSIONS,
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { ConversationCountDrillDownBaseQuery } from 'cube/queries/ConversationCountDrillDown.query'
import type { PointClickEventObject } from 'highcharts'
import moment from 'moment'

import { Card, Flex, Header, Text } from '@driftt/dds'
import {
  CubeQueryBuilder,
  CubeQueryStringAdapter,
  getStartOfSeriesTimestamp,
  LineChart,
} from '@driftt/dds.reports'

import { useTrackDrillDownClickEvent } from 'components/conversation/hooks/useTrackDrilldownClickEvent'
import useDateRange from 'hooks/useDateRange'
import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'
import { getHumanizedDuration } from 'utils/format'

import { useConversationMedianTimeData } from './useConversationMedianTimeData.hook'

export const ConversationMedianTimeChartBeta = () => {
  const { legendText } = useDateRange()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()

  const { trackViewEvent } = useTrackDrillDownClickEvent(
    'ConversationMedianTimeLineChart'
  )

  const {
    metric,
    previousMetric,
    isLoading,
    metricConversationCounts,
    previousMetricConversationCounts,
  } = useConversationMedianTimeData()

  const onChartPointClick = useCallback(
    (evt: PointClickEventObject) => {
      if (evt.point.y === 0) {
        return
      }

      const startDate = moment(evt.point.name).startOf('D')
      const endDate = moment(evt.point.name).endOf('D')

      const hydratedDrillDownQuery = new CubeQueryBuilder({
        query: ConversationCountDrillDownBaseQuery,
      })
        .withName('ConversationMedianTimeDrillDownPoint')
        .withinDateRange(
          CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
          startDate.toDate(),
          endDate.toDate()
        )
        .withFilters([...pageFilters, ...accessFilters])
        .build()

      const totalRecords =
        evt.point.series.name === legendText.series
          ? metricConversationCounts[evt.point.index]
          : previousMetricConversationCounts[evt.point.index]

      // Track drill down click
      trackViewEvent({ totalRecords })

      const drillDownQueryString = CubeQueryStringAdapter.toQueryString(
        hydratedDrillDownQuery
      )
      const drillDownURL = `${CONVO_DETAIL_REPORT_PATH}?${drillDownQueryString}`
      window.open(drillDownURL, '_blank')
    },
    [
      legendText.series,
      metricConversationCounts,
      previousMetricConversationCounts,
      pageFilters,
      accessFilters,
      trackViewEvent,
    ]
  )

  return (
    <Card
      rootProps={{
        id: 'chats-report--median-conversation-length-card',
      }}
    >
      <Card.Header>
        <Header variant="h3">Median chat length</Header>
        <Text variant="metadata">
          What is the median time from a chat first open to first close?
        </Text>
      </Card.Header>
      <Card.Content as={Flex} fullHeight>
        <LineChart
          isLoading={isLoading}
          onPointClick={onChartPointClick}
          series={[
            {
              data: metric,
              label: legendText.series,
              pointStart: getStartOfSeriesTimestamp(metric),
            },
            {
              data: previousMetric,
              label: legendText.previousSeries,
              dashStyle: 'LongDash',
              pointStart: getStartOfSeriesTimestamp(metric),
            },
          ]}
          options={{
            yAxis: {
              labels: {
                formatter: function () {
                  return getHumanizedDuration(this.value, 'seconds')
                },
              },
            },
          }}
          tooltipYValueFormat="DURATION_FROM_SECONDS_TO_MIN_SEC"
          tooltipXValueFormat="DATE"
          hideTooltipHeader
        />
      </Card.Content>
    </Card>
  )
}
